import { SparklesIcon } from '@heroicons/react/24/outline'
import React from 'react'

/**
 * HeroSection component displays the main landing section with call-to-action buttons.
 *
 * Features:
 * - Animated background decorations
 * - Smooth scroll to contact section
 * - Environment-based configuration for external links
 * - Responsive design with mobile-first approach
 * - Performance statistics display
 *
 * @component
 * @example
 * ```tsx
 * <HeroSection />
 * ```
 */
const HeroSection: React.FC = () => {
  /**
   * Smoothly scrolls to the contact section when called
   */
  const scrollToContact = (): void => {
    const element = document.getElementById('contact')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <section
      className="relative py-20 sm:py-32 bg-gradient-to-br from-primary-50 to-secondary-100 overflow-hidden mt-16"
      aria-label="Hero section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30" aria-hidden="true">
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl animate-float"></div>
        <div
          className="absolute top-10 right-10 w-72 h-72 bg-secondary-200 rounded-full mix-blend-multiply filter blur-xl animate-float"
          style={{ animationDelay: '2s' }}
        ></div>
        <div
          className="absolute -bottom-8 left-20 w-72 h-72 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl animate-float"
          style={{ animationDelay: '4s' }}
        ></div>
      </div>

      <div className="relative container-padding text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-6xl font-bold text-secondary-900 mb-6 animate-slide-up">
            Connect Special Education Students with{' '}
            <span className="text-gradient">Qualified Therapy Providers</span>
          </h1>
          <p className="text-xl sm:text-2xl text-secondary-600 mb-8 max-w-3xl mx-auto animate-slide-up animate-delay-100">
            TherapyLink helps schools, NPAs, and therapy companies manage student service requests,
            match providers by fit and availability, and move outreach forward in a secure,
            FERPA-aligned workflow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up animate-delay-200">
            <button
              onClick={scrollToContact}
              className="btn-primary text-lg px-8 py-4"
              aria-label="Request a demo of TherapyLink platform"
            >
              <SparklesIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              Request Demo
            </button>
          </div>

          {/* Stats Section */}
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto"
            role="region"
            aria-label="Platform statistics"
          >
            <div className="text-center animate-slide-up animate-delay-300">
              <div className="text-3xl font-bold text-primary-600 mb-2">95%</div>
              <div className="text-secondary-600">Faster Matching</div>
            </div>
            <div className="text-center animate-slide-up animate-delay-300">
              <div className="text-3xl font-bold text-primary-600 mb-2">25+</div>
              <div className="text-secondary-600">Provider Outreach Steps Automated</div>
            </div>
            <div className="text-center animate-slide-up animate-delay-400">
              <div className="text-3xl font-bold text-primary-600 mb-2">100%</div>
              <div className="text-secondary-600">FERPA Compliant</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection
